/**
 * Device Detection Utilities
 * 
 * Provides functions to detect device types (mobile, tablet, desktop)
 * using a combination of user agent strings and screen dimensions.
 */

export type DeviceType = 'mobile' | 'tablet' | 'desktop';

export interface DeviceInfo {
    type: DeviceType;
    isMobile: boolean;
    isTablet: boolean;
    isDesktop: boolean;
    isTouchDevice: boolean;
    screenWidth: number;
    screenHeight: number;
    userAgent: string;
    platform: string;
    orientation: 'portrait' | 'landscape';
}

// Breakpoints based on common device sizes and your Tailwind config
const BREAKPOINTS = {
    MOBILE_MAX: 767,    // Up to md breakpoint (768px) - 1
    TABLET_MIN: 768,    // md breakpoint
    TABLET_MAX: 1023,   // Up to lg breakpoint (1024px) - 1
    DESKTOP_MIN: 1024   // lg breakpoint and above
} as const;

// User Agent patterns for different device types
const USER_AGENT_PATTERNS = {
    mobile: [
        /Android.*Mobile/i,
        /iPhone/i,
        /iPod/i,
        /Windows Phone/i,
        /BlackBerry/i,
        /BB10/i,
        /Mobile/i
    ],
    tablet: [
        /iPad/i,
        /Android(?!.*Mobile)/i,
        /Tablet/i,
        /PlayBook/i,
        /Kindle/i
    ]
} as const;

/**
 * Detect device type based on user agent string
 */
function detectDeviceFromUserAgent(userAgent: string): DeviceType {
    // Check for mobile patterns first (more specific)
    if (USER_AGENT_PATTERNS.mobile.some(pattern => pattern.test(userAgent))) {
        return 'mobile';
    }
    
    // Check for tablet patterns
    if (USER_AGENT_PATTERNS.tablet.some(pattern => pattern.test(userAgent))) {
        return 'tablet';
    }
    
    // Default to desktop
    return 'desktop';
}

/**
 * Detect device type based on screen dimensions
 */
function detectDeviceFromScreenSize(width: number, height: number): DeviceType {
    // For better desktop detection, use the larger dimension instead of smaller
    // This prevents desktop browsers with small viewport from being classified as tablets
    const maxDimension = Math.max(width, height);
    
    // Also check if either dimension meets desktop threshold
    const hasDesktopWidth = width >= BREAKPOINTS.DESKTOP_MIN;
    const hasDesktopHeight = height >= BREAKPOINTS.DESKTOP_MIN;
    
    // If either dimension is desktop-sized, consider it desktop
    if (hasDesktopWidth || hasDesktopHeight || maxDimension >= BREAKPOINTS.DESKTOP_MIN) {
        return 'desktop';
    }
    
    // Use the smaller dimension for mobile/tablet distinction
    const minDimension = Math.min(width, height);
    
    if (minDimension <= BREAKPOINTS.MOBILE_MAX) {
        return 'mobile';
    } else {
        return 'tablet';
    }
}

/**
 * Check if device has touch capabilities
 */
function isTouchDevice(): boolean {
    return (
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        // @ts-ignore - older browsers
        navigator.msMaxTouchPoints > 0
    );
}

/**
 * Get current screen orientation
 */
function getOrientation(width: number, height: number): 'portrait' | 'landscape' {
    return width > height ? 'landscape' : 'portrait';
}

/**
 * Get platform information
 */
function getPlatform(): string {
    if (typeof navigator !== 'undefined') {
        // Use modern userAgentData if available
        if ('userAgentData' in navigator && navigator.userAgentData?.platform) {
            return navigator.userAgentData.platform;
        }
        
        // Fallback: extract platform from user agent
        const ua = navigator.userAgent;
        if (/Mac|iPhone|iPad|iPod/.test(ua)) return 'macOS';
        if (/Win/.test(ua)) return 'Windows';
        if (/Linux/.test(ua)) return 'Linux';
        if (/Android/.test(ua)) return 'Android';
        
        return 'Unknown';
    }
    return 'Unknown';
}

/**
 * Main device detection function
 * Combines user agent and screen size detection for more accurate results
 */
export function detectDevice(): DeviceInfo {
    if (typeof window === 'undefined') {
        // Server-side fallback
        return {
            type: 'desktop',
            isMobile: false,
            isTablet: false,
            isDesktop: true,
            isTouchDevice: false,
            screenWidth: 1920,
            screenHeight: 1080,
            userAgent: '',
            platform: 'Unknown',
            orientation: 'landscape'
        };
    }

    const userAgent = navigator.userAgent;
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Use viewport dimensions for more accurate detection
    const width = viewportWidth || screenWidth;
    const height = viewportHeight || screenHeight;
    
    // Get device type from both methods
    const userAgentDevice = detectDeviceFromUserAgent(userAgent);
    const screenSizeDevice = detectDeviceFromScreenSize(width, height);
    
    // Improved detection logic:
    // 1. Trust user agent for mobile devices (most reliable)
    // 2. For desktop detection, prioritize screen size over user agent
    // 3. Use screen size as tie-breaker for ambiguous cases
    let finalDeviceType: DeviceType;
    
    if (userAgentDevice === 'mobile') {
        // Mobile user agents are very reliable
        finalDeviceType = 'mobile';
    } else if (screenSizeDevice === 'desktop') {
        // If screen size indicates desktop, trust it (even if UA says tablet)
        finalDeviceType = 'desktop';
    } else if (userAgentDevice === 'tablet') {
        // Only use tablet from UA if screen size doesn't suggest desktop
        finalDeviceType = 'tablet';
    } else {
        // Fallback to screen size detection
        finalDeviceType = screenSizeDevice;
    }
    
    return {
        type: finalDeviceType,
        isMobile: finalDeviceType === 'mobile',
        isTablet: finalDeviceType === 'tablet',
        isDesktop: finalDeviceType === 'desktop',
        isTouchDevice: isTouchDevice(),
        screenWidth: width,
        screenHeight: height,
        userAgent,
        platform: getPlatform(),
        orientation: getOrientation(width, height)
    };
}

/**
 * Simple boolean check functions for convenience
 */
export function isMobileDevice(): boolean {
    return detectDevice().isMobile;
}

export function isTabletDevice(): boolean {
    return detectDevice().isTablet;
}

export function isDesktopDevice(): boolean {
    return detectDevice().isDesktop;
}

export function isTouchCapable(): boolean {
    return detectDevice().isTouchDevice;
}

/**
 * Get just the device type string
 */
export function getDeviceType(): DeviceType {
    return detectDevice().type;
}

/**
 * Check if device meets minimum screen size requirements
 */
export function meetsMinimumSize(minWidth: number, minHeight?: number): boolean {
    const { screenWidth, screenHeight } = detectDevice();
    const heightCheck = minHeight ? screenHeight >= minHeight : true;
    return screenWidth >= minWidth && heightCheck;
}

/**
 * Check if device is in specific orientation
 */
export function isLandscape(): boolean {
    return detectDevice().orientation === 'landscape';
}

export function isPortrait(): boolean {
    return detectDevice().orientation === 'portrait';
}

/**
 * Advanced device detection with custom breakpoints
 */
export function detectDeviceWithCustomBreakpoints(
    mobileMax: number = BREAKPOINTS.MOBILE_MAX,
    tabletMax: number = BREAKPOINTS.TABLET_MAX
): DeviceInfo {
    const deviceInfo = detectDevice();
    const { screenWidth, screenHeight } = deviceInfo;
    const minDimension = Math.min(screenWidth, screenHeight);
    
    let customType: DeviceType;
    if (minDimension <= mobileMax) {
        customType = 'mobile';
    } else if (minDimension <= tabletMax) {
        customType = 'tablet';
    } else {
        customType = 'desktop';
    }
    
    return {
        ...deviceInfo,
        type: customType,
        isMobile: customType === 'mobile',
        isTablet: customType === 'tablet',
        isDesktop: customType === 'desktop'
    };
}

// Export breakpoints for reference
export { BREAKPOINTS }; 